'use client';

import type { Table } from '@/lib/table-features';
import { useTranslations } from 'next-intl';
import { CheckCircle2 } from 'lucide-react';
import { useHashDialog } from '@/hooks/use-hash-dialog';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog';
import type { Registration } from '@/features/registration';
import { REVIEW_HASH } from '../constants';
import { useApproveStageMutation } from '../services/review.mutations';

interface ReviewTableActionBarProps {
  table: Table<Registration>;
  isAllPagesSelected: boolean;
  totalCount: number;
}

export function ReviewTableActionBar({
  table,
  isAllPagesSelected,
  totalCount
}: ReviewTableActionBarProps) {
  const t = useTranslations('review');
  const { open: approveOpen, onOpenChange: toggleApprove } = useHashDialog(
    REVIEW_HASH.bulkApprove
  );
  const approveMutation = useApproveStageMutation();

  const selectedRows = table.getFilteredSelectedRowModel().rows;
  const selectedCount = isAllPagesSelected ? totalCount : selectedRows.length;

  if (selectedCount === 0) return null;

  const handleBulkApprove = async () => {
    const ids = selectedRows.map((row) => row.original.id);
    for (const id of ids) {
      await approveMutation.mutateAsync(id);
    }
    table.resetRowSelection();
    toggleApprove(false);
  };

  return (
    <>
      <div className='bg-muted flex items-center gap-3 rounded-lg px-4 py-2'>
        <span className='text-muted-foreground text-sm font-medium'>
          {t('selectedCount', { count: selectedCount })}
        </span>
        <div className='flex items-center gap-2'>
          <Button
            variant='outline'
            size='sm'
            className='text-green-600 hover:text-green-700'
            onClick={() => toggleApprove(true)}
            disabled={isAllPagesSelected}
          >
            <CheckCircle2 className='me-1.5 h-3.5 w-3.5' />
            {t('approveSelected')}
          </Button>
        </div>
      </div>

      <AlertDialog open={approveOpen} onOpenChange={toggleApprove}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('bulkApproveTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('bulkApproveDescription', { count: selectedCount })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleBulkApprove}
              className='bg-green-600 text-white hover:bg-green-700'
            >
              {t('confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
